import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import store, { showToast, showToastWithType } from '../../../../core/store';
import { deleteCasePlanWidget, moveCasePlanWidget } from '../../api/casePlanDetailApi';
import {
  CASE_PLAN_WIDGET_ERROR_MESSAGES,
  CASE_PLAN_WIDGET_REMOVAL_REQUESTS,
  CASE_PLAN_WIDGET_STATUSES,
} from '../../constants/reentry-planning.constants';
import { isStaleSectionStateError } from '../../utils/case-plan-section-error.util';
import { findSectionById, isPendingSection } from '../../utils/case-plan-section.util';
import {
  filterActiveWidgets,
  getWidgetMoveDestinationSections,
  isActiveWidget,
  isDeletedWidget,
  moveWidgetInSection,
  moveWidgetToSection,
  reinsertWidgetAtIndex,
  revertWidgetMoveToSection,
  stripWidgetMenuOptions,
} from '../../utils/case-plan-widget.util';
import {
  mapWidget,
  removeWidgetFromSection,
} from '../../utils/reentry-plan-detail.util';

const shouldKeepWidgetRemoved = (request, response) =>
  request.dropOnSuccess || isDeletedWidget(response);

const getWidgetIndex = (sections, sectionId, widgetId) =>
  (findSectionById(sections, sectionId)?.widgets ?? []).findIndex((widget) => widget.id === widgetId);

/** Stored positions of the widgets a section renders — archived and deleted ones stay hidden. */
const getRenderedWidgetIndexes = (widgets) =>
  widgets.flatMap((widget, index) => (isActiveWidget(widget) ? [index] : []));

/**
 * Re-derives the widget index from React's current section state so optimistic updates and
 * rollbacks do not apply a rendered index to a newer list. `delta` counts rendered widgets, so
 * hidden ones are stepped over and left at their stored positions.
 */
const moveWidgetById = (sections, sectionId, widgetId, delta) => {
  const widgets = findSectionById(sections, sectionId)?.widgets ?? [];
  const renderedIndexes = getRenderedWidgetIndexes(widgets);
  const fromPosition = renderedIndexes.findIndex((index) => widgets[index].id === widgetId);
  const toPosition = fromPosition + delta;

  if (fromPosition === -1 || toPosition < 0 || toPosition >= renderedIndexes.length) {
    return sections;
  }

  return moveWidgetInSection(
    sections,
    sectionId,
    renderedIndexes[fromPosition],
    renderedIndexes[toPosition],
  );
};

/**
 * Optimistic widget reorder and cross-section move. Mirrors section mutations: the list
 * updates on click, the request runs behind it, and a rejection rolls back only the swap
 * that failed. Transforms run against React's current list (widget found by id) so a reload
 * cannot be overwritten with a stale snapshot, and a second move is ignored until the first
 * PATCH settles so rendered indexes cannot be applied twice.
 *
 * @param {{
 *   casePlanId: string,
 *   sections: object[],
 *   setSections: (sections: object[] | ((current: object[]) => object[])) => void,
 *   reloadDetail: () => void,
 * }} params
 */
export const useCasePlanWidgetMutations = ({
  casePlanId,
  sections,
  setSections,
  reloadDetail,
}) => {
  const [pendingMoveToSection, setPendingMoveToSection] = useState(null);
  const pendingWidgetMoveRef = useRef(false);

  const sectionsRef = useRef(sections);
  useLayoutEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  const updateSections = useCallback(
    (updateList) => {
      setSections((current) => {
        const nextSections = updateList(current);
        sectionsRef.current = nextSections;
        return nextSections;
      });
    },
    [setSections],
  );

  const handleMoveFailure = useCallback(
    (error, rollbackSections = null) => {
      if (rollbackSections) {
        updateSections(rollbackSections);
      }

      store.dispatch(showToast(CASE_PLAN_WIDGET_ERROR_MESSAGES.MOVE));

      if (isStaleSectionStateError(error)) {
        reloadDetail();
      }
    },
    [reloadDetail, updateSections],
  );

  const handleRemovalFailure = useCallback(
    (error, errorMessage, rollbackSections = null) => {
      if (rollbackSections) {
        updateSections(rollbackSections);
      }

      store.dispatch(showToast(errorMessage));

      if (isStaleSectionStateError(error)) {
        reloadDetail();
      }
    },
    [reloadDetail, updateSections],
  );

  const moveWidget = useCallback(
    async (sectionId, fromIndex, toIndex) => {
      if (pendingWidgetMoveRef.current || fromIndex === toIndex) {
        return;
      }

      const currentSections = sectionsRef.current;
      const targetSection = findSectionById(currentSections, sectionId);

      if (!targetSection || isPendingSection(targetSection)) {
        return;
      }

      // Up/Down indexes come from the rendered list, which hides archived and deleted widgets.
      const renderedWidgets = filterActiveWidgets(targetSection.widgets);
      const widgetId = renderedWidgets[fromIndex]?.id;

      if (!widgetId || toIndex < 0 || toIndex >= renderedWidgets.length) {
        return;
      }

      const delta = toIndex - fromIndex;

      pendingWidgetMoveRef.current = true;

      updateSections((current) => moveWidgetById(current, sectionId, widgetId, delta));

      try {
        await moveCasePlanWidget(casePlanId, widgetId, { sortOrder: toIndex });
      } catch (error) {
        handleMoveFailure(error, (current) => moveWidgetById(current, sectionId, widgetId, -delta));
      } finally {
        pendingWidgetMoveRef.current = false;
      }
    },
    [casePlanId, handleMoveFailure, updateSections],
  );

  const openMoveToSectionModal = useCallback((widgetId, fromSectionId) => {
    if (!widgetId || !fromSectionId) {
      return;
    }

    setPendingMoveToSection({ widgetId, fromSectionId });
  }, []);

  const closeMoveToSectionModal = useCallback(() => {
    setPendingMoveToSection(null);
  }, []);

  const confirmMoveToSection = useCallback(
    async (toSectionId) => {
      const request = pendingMoveToSection;

      if (
        pendingWidgetMoveRef.current ||
        !request?.widgetId ||
        !request?.fromSectionId ||
        !toSectionId
      ) {
        return;
      }

      const originalIndex = getWidgetIndex(
        sectionsRef.current,
        request.fromSectionId,
        request.widgetId,
      );

      if (originalIndex === -1) {
        return;
      }

      if (
        moveWidgetToSection(
          sectionsRef.current,
          request.fromSectionId,
          toSectionId,
          request.widgetId,
        ) === sectionsRef.current
      ) {
        return;
      }

      pendingWidgetMoveRef.current = true;
      setPendingMoveToSection(null);

      updateSections((current) =>
        moveWidgetToSection(current, request.fromSectionId, toSectionId, request.widgetId),
      );

      try {
        await moveCasePlanWidget(casePlanId, request.widgetId, { sectionId: toSectionId });
      } catch (error) {
        handleMoveFailure(error, (current) =>
          revertWidgetMoveToSection(
            current,
            request.fromSectionId,
            toSectionId,
            request.widgetId,
            originalIndex,
          ),
        );
      } finally {
        pendingWidgetMoveRef.current = false;
      }
    },
    [casePlanId, handleMoveFailure, pendingMoveToSection, updateSections],
  );

  const sendWidgetRemoval = useCallback(
    async (sectionId, widget, action, index) => {
      const request = CASE_PLAN_WIDGET_REMOVAL_REQUESTS[action];

      if (!request || !widget?.id) {
        return;
      }

      try {
        const response = mapWidget(await deleteCasePlanWidget(casePlanId, widget.id));

        if (!shouldKeepWidgetRemoved(request, response)) {
          const archivedWidget = stripWidgetMenuOptions({
            ...widget,
            ...response,
            status: response.status ?? CASE_PLAN_WIDGET_STATUSES.ARCHIVED,
          });
          updateSections((current) => reinsertWidgetAtIndex(current, sectionId, archivedWidget, index));
        }

        store.dispatch(showToastWithType(request.successMessage, 'success'));
      } catch (error) {
        handleRemovalFailure(error, request.errorMessage, (current) =>
          reinsertWidgetAtIndex(current, sectionId, widget, index),
        );
      }
    },
    [casePlanId, handleRemovalFailure, updateSections],
  );

  const removeWidget = useCallback(
    async (sectionId, widget, action) => {
      if (pendingWidgetMoveRef.current || !CASE_PLAN_WIDGET_REMOVAL_REQUESTS[action]) {
        return;
      }

      const currentSections = sectionsRef.current;
      const section = findSectionById(currentSections, sectionId);
      const index = getWidgetIndex(currentSections, sectionId, widget?.id);

      // UI never opens removal on a pending section; this guards against stale callbacks.
      if (isPendingSection(section) || index === -1) {
        return;
      }

      updateSections((current) => removeWidgetFromSection(current, sectionId, widget.id));

      await sendWidgetRemoval(sectionId, widget, action, index);
    },
    [sendWidgetRemoval, updateSections],
  );

  const getMoveDestinations = useCallback(
    (sectionId) => getWidgetMoveDestinationSections(sections, sectionId),
    [sections],
  );

  return {
    moveWidget,
    removeWidget,
    openMoveToSectionModal,
    closeMoveToSectionModal,
    confirmMoveToSection,
    getMoveDestinations,
    moveToSectionModal: {
      isOpen: Boolean(pendingMoveToSection),
      destinations: pendingMoveToSection
        ? getWidgetMoveDestinationSections(sections, pendingMoveToSection.fromSectionId)
        : [],
    },
  };
};
